import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, CalendarClock, Loader2, Mail, MailCheck, MessageSquareText, Sparkles, Users2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useUpdateAgent, useUserAgents } from "@/hooks/agent";
import { useEmailTemplateConfig, useEmailTemplateOperations, useEmailTemplatePreview } from "@/hooks/useEmailTemplates";
import { emailTemplateService } from "@/services/communication/EmailTemplateService";
import { EmailTemplateType } from "@/types/email-templates";
import { DEFAULT_SIGNATURE_STYLE, GenieBranding } from "@/types/ui/branding";
import ActionButtons from "./components/ActionButtons";
import AgentSelector from "./components/AgentSelector";
import BlockEditor from "./components/BlockEditor";
import BrandingSettings from "./components/BrandingSettings";
import ButtonSettings from "./components/ButtonSettings";
import CloneDialog from "./components/CloneDialog";
import ColorSettings from "./components/ColorSettings";
import EmailPreview from "./components/EmailPreview";
import FontSettings from "./components/FontSettings";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

type TemplateType = EmailTemplateType;

interface TemplateTabMeta {
  value: TemplateType;
  label: string;
  icon: typeof Mail;
  recipient: "you" | "customer";
  description: string;
}

const TEMPLATE_GROUPS: { title: string; recipient: "you" | "customer"; tabs: TemplateTabMeta[] }[] = [
  {
    title: "Sent to you",
    recipient: "you",
    tabs: [
      {
        value: "conversation_report",
        label: "Conversation Reports",
        icon: Mail,
        recipient: "you",
        description: "Sent to your team after every conversation. Full metrics, transcript, and AI insights.",
      },
      {
        value: "escalation_alert",
        label: "Escalation Alerts",
        icon: AlertTriangle,
        recipient: "you",
        description: "Sent to your team the moment a conversation needs human attention.",
      },
      {
        value: "conversation_digest",
        label: "Conversation Digests",
        icon: CalendarClock,
        recipient: "you",
        description: "Sent to your team on a schedule — a batched summary of all conversations in the period.",
      },
      {
        value: "conversation_auto_digest",
        label: "Auto Decision Summaries",
        icon: Sparkles,
        recipient: "you",
        description: "Sent to your team when Report Mode is set to \"Auto\" — the AI's triage of what needs attention, was handled automatically, or was irrelevant.",
      },
      {
        value: "view_report",
        label: "View Reports",
        icon: MailCheck,
        recipient: "you",
        description: "Sent to your team as a recap when a saved data view or report is run.",
      },
    ],
  },
  {
    title: "Sent to customers & attendees",
    recipient: "customer",
    tabs: [
      {
        value: "customer_report",
        label: "Customer Reports",
        icon: MessageSquareText,
        recipient: "customer",
        description: "Sent to the customer after their conversation — a friendly recap with next steps and a call-to-action.",
      },
      {
        value: "meeting_recap",
        label: "Meeting Recaps",
        icon: Users2,
        recipient: "customer",
        description: "Sent to meeting attendees — decisions made, action items, and recommendations.",
      },
    ],
  },
];

const ALL_TEMPLATE_TABS = TEMPLATE_GROUPS.flatMap((group) => group.tabs);

const EmailTemplatesPage = () => {
  const { userProfile, team } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>("conversation_report");
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const { data: agents = [] } = useUserAgents();
  const selectedAgent = agents.find((a) => a.id === selectedAgentId);
  const selectedTemplateMeta = ALL_TEMPLATE_TABS.find((tab) => tab.value === selectedTemplate);

  const REPORT_MODE_TEMPLATES: TemplateType[] = [
    "conversation_report",
    "escalation_alert",
    "conversation_digest",
    "conversation_auto_digest",
  ];

  const settingsLink =
    selectedTemplate === "view_report"
      ? { to: "/dashboard?tab=views", label: "Edit Views" }
      : REPORT_MODE_TEMPLATES.includes(selectedTemplate) && selectedAgentId
        ? { to: `/genie-hub/my-genies/${selectedAgentId}?section=section-mailbox`, label: "Edit Report Mode" }
        : null;

  const [agentIdsWithTemplates, setAgentIdsWithTemplates] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!userProfile?.id) return;
    emailTemplateService
      .listTemplates(userProfile.id)
      .then((items) => {
        setAgentIdsWithTemplates(new Set(items.filter((t) => t.agent_id).map((t) => t.agent_id as string)));
      })
      .catch((error) => {
        console.error("Failed to load email template summary:", error);
      });
  }, [userProfile?.id]);

  const agentEmailStatuses = useMemo(() => {
    const statuses = new Map<string, { hasTemplate: boolean; hasBranding: boolean; hasSignature: boolean }>();
    agents.forEach((agent) => {
      const branding = agent.branding as GenieBranding | null | undefined;
      const hasBranding = !!(
        branding?.primaryColor ||
        branding?.secondaryColor ||
        branding?.textColor ||
        branding?.backgroundColor ||
        branding?.logoUrl
      );
      statuses.set(agent.id, {
        hasTemplate: agentIdsWithTemplates.has(agent.id),
        hasBranding,
        hasSignature: !!branding?.signatureStyle?.enabled,
      });
    });
    return statuses;
  }, [agents, agentIdsWithTemplates]);

  const {
    config,
    updateConfig,
    handleColorChange,
    handleFontChange,
    handleGoogleFontChange,
    handleBrandingChange,
    handleButtonChange,
    resetToDefaults,
    openSections,
    toggleSection,
    GOOGLE_FONTS,
    toggleBlock,
    reorderBlocks,
    updateBlockLabel,
    updateBlockSettings,
    addBlock,
    removeBlock,
  } = useEmailTemplateConfig();

  const previewContext = useMemo(
    () => ({
      agentName: selectedAgent?.name,
      agentDescription: selectedAgent?.description,
      agentBranding: (selectedAgent?.branding as GenieBranding | null) ?? null,
      teamName: team?.name,
      teamBranding: (team?.branding as unknown as GenieBranding | null) ?? null,
    }),
    [selectedAgent, team],
  );

  const { previewHtml } = useEmailTemplatePreview(selectedTemplate, config, selectedAgent?.redirect_url, previewContext);

  const { saving, cloning, showCloneDialog, setShowCloneDialog, handleSave, handleApplyBranding, handleCloneDefault } =
    useEmailTemplateOperations({
      userProfileId: userProfile?.id,
      selectedAgentId,
      agents,
      teamBranding: !selectedAgentId && team?.branding ? (team.branding as unknown as any) : undefined,
    });

  const updateAgent = useUpdateAgent();

  // Turning on the "Email Signature" block here is meaningless unless the
  // selected genie's OWN branding.signatureStyle.enabled is also on — that's
  // the one flag every send-time renderer actually reads (see
  // _signature-block.ts). A genie that never opened the Branding modal has no
  // signatureStyle at all, so without this the block would show as "on" in
  // this editor while silently rendering nothing. Firing a single explicit
  // write here (same tier of action as clicking Save Branding) keeps the two
  // pages' toggles in sync instead of leaving a confusing no-op.
  const handleToggleBlock = (blockId: string, enabled: boolean) => {
    toggleBlock(blockId, enabled);

    if (blockId !== "signature" || !enabled || !selectedAgent) return;

    const branding = (selectedAgent.branding as GenieBranding | null) ?? null;
    if (branding?.signatureStyle?.enabled) return;

    updateAgent.mutate(
      {
        agent: selectedAgent,
        updateData: {
          branding: {
            ...(branding || {}),
            signatureStyle: { ...DEFAULT_SIGNATURE_STYLE, ...(branding?.signatureStyle || {}), enabled: true },
          },
        },
      },
      {
        onError: (error: any) => {
          console.error("Failed to enable genie signature style:", error);
          toast.error("Enabled the block, but couldn't turn on this genie's signature — try Branding instead");
        },
      },
    );
  };

  useEffect(() => {
    loadTemplateConfig();
  }, [userProfile?.id, selectedTemplate, selectedAgentId]);

  const loadTemplateConfig = async () => {
    if (!userProfile?.id) return;

    try {
      setLoading(true);
      const loadedConfig = await emailTemplateService.getTemplateConfig(userProfile.id, selectedTemplate, selectedAgentId);
      console.log("[EmailTemplates] loaded config from DB", { selectedTemplate, selectedAgentId, loadedConfig });

      updateConfig(loadedConfig, selectedTemplate);
    } catch (error) {
      console.error("Failed to load template config:", error);
      toast.error("Failed to load template configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClick = () => {
    handleSave(selectedTemplate, config);
  };

  const handleApplyBrandingClick = () => {
    handleApplyBranding(config, (newConfig) => updateConfig(newConfig, selectedTemplate));
  };

  const handleCloneDefaultClick = () => {
    handleCloneDefault(selectedTemplate, (newConfig) => updateConfig(newConfig, selectedTemplate));
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[#F9FAFC]">
        <div className="container mx-auto px-4 py-8 max-w-[1920px]">
          <div className="w-full">
            <div className="flex items-center mb-8">
              <GenieIcon name="wifi" size={28} label="Email Templates" className="h-7 w-7 md:h-8 md:w-8 mr-3 md:mr-4" />
              <div>
                <h1 className="text-3xl font-bold text-[#222B45]">Email Templates</h1>
                <p className="text-[#64748B] mt-1">
                  Customize the look and feel of your email reports. Configure colors, fonts, sections, and branding to match your brand.
                </p>
              </div>
            </div>

            <AgentSelector
              agents={agents}
              selectedAgentId={selectedAgentId}
              onAgentSelect={setSelectedAgentId}
              agentEmailStatuses={agentEmailStatuses}
            />

            <Tabs value={selectedTemplate} onValueChange={(v) => setSelectedTemplate(v as TemplateType)} className="space-y-3">
              <div className="space-y-3">
                {TEMPLATE_GROUPS.map((group) => (
                  <div key={group.title}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8] mb-1.5">{group.title}</p>
                    <div className="overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
                      <TabsList className="w-max">
                        {group.tabs.map((tab) => (
                          <TabsTrigger key={tab.value} value={tab.value} className="flex items-center gap-2 whitespace-nowrap">
                            <tab.icon className="h-4 w-4 shrink-0" />
                            {tab.label}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                    </div>
                  </div>
                ))}
              </div>

              {selectedTemplateMeta && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <p className="text-sm text-[#64748B]">{selectedTemplateMeta.description}</p>
                  {settingsLink && (
                    <Link
                      to={settingsLink.to}
                      className="inline-flex items-center gap-1 text-sm font-medium text-genie-primary hover:underline whitespace-nowrap"
                    >
                      {settingsLink.label}
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Configuration Panel */}
                  <div className="lg:col-span-1 space-y-6">
                    <ColorSettings
                      config={config}
                      isOpen={openSections.colors}
                      onToggle={() => toggleSection("colors")}
                      onColorChange={handleColorChange}
                      agentBranding={selectedAgent?.branding as GenieBranding | null}
                      agentName={selectedAgent?.name}
                    />

                    <FontSettings
                      config={config}
                      isOpen={openSections.fonts}
                      onToggle={() => toggleSection("fonts")}
                      onFontChange={handleFontChange}
                      onGoogleFontChange={handleGoogleFontChange}
                      googleFonts={GOOGLE_FONTS}
                    />

                    <BrandingSettings
                      config={config}
                      isOpen={openSections.branding}
                      onToggle={() => toggleSection("branding")}
                      onBrandingChange={handleBrandingChange}
                      selectedAgent={
                        selectedAgent
                          ? {
                              id: selectedAgent.id,
                              name: selectedAgent.name,
                              branding: selectedAgent.branding as GenieBranding | null,
                            }
                          : null
                      }
                      onApplyAgentBranding={handleApplyBrandingClick}
                      teamBranding={!selectedAgentId && team?.branding ? (team.branding as unknown as GenieBranding | null) : null}
                      onApplyTeamBranding={!selectedAgentId ? handleApplyBrandingClick : undefined}
                    />

                    <ButtonSettings
                      config={config}
                      isOpen={openSections.buttons}
                      onToggle={() => toggleSection("buttons")}
                      onButtonChange={handleButtonChange}
                      isEnabled={!!selectedAgent?.redirect_url}
                    />

                    <BlockEditor
                      blocks={config.blocks}
                      templateType={selectedTemplate}
                      onToggleBlock={handleToggleBlock}
                      onReorderBlocks={reorderBlocks}
                      onUpdateLabel={updateBlockLabel}
                      onUpdateSettings={updateBlockSettings}
                      onAddBlock={addBlock}
                      onRemoveBlock={removeBlock}
                    />

                    <ActionButtons
                      onSave={handleSaveClick}
                      onClone={() => setShowCloneDialog(true)}
                      onReset={() => resetToDefaults(selectedTemplate)}
                      isSaving={saving}
                      isLoading={loading}
                    />
                  </div>

                  {/* Preview Section */}
                  <div className="lg:col-span-2">
                    <EmailPreview previewHtml={previewHtml} templateType={selectedTemplate} />
                  </div>
                </div>
              )}
            </Tabs>
          </div>
        </div>
      </div>

      <CloneDialog
        isOpen={showCloneDialog}
        onOpenChange={setShowCloneDialog}
        onClone={handleCloneDefaultClick}
        isCloning={cloning}
        templateType={selectedTemplate}
        selectedAgentName={selectedAgent?.name}
        selectedAgentId={selectedAgentId}
      />
    </TooltipProvider>
  );
};

export default EmailTemplatesPage;
